/**
 * Snackbar Context - 全局信息提示
 * 使用 MUI Snackbar + Alert，通过 Context + Hook 供任意组件调用
 */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { Snackbar, Alert, type AlertColor } from "@mui/material";
import {
  ELEVATED_DIALOG_Z_INDEX_OFFSET,
  GLOBAL_SNACKBAR_ABOVE_ELEVATED_Z_INDEX_OFFSET,
} from "../constants/dialogZIndex";

/** Default snackbar auto-hide duration (ms). */
export const DEFAULT_SNACKBAR_AUTO_HIDE_MS = 3000;

export interface SnackbarContextType {
  showMessage: (
    message: string,
    severity?: AlertColor,
    autoHideDurationMs?: number,
  ) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined
);

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};

export const SnackbarProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState<AlertColor>("info");
  const [autoHideDuration, setAutoHideDuration] = useState(
    DEFAULT_SNACKBAR_AUTO_HIDE_MS,
  );

  const showMessage = useCallback(
    (
      msg: string,
      sev: AlertColor = "info",
      autoHideDurationMs: number = DEFAULT_SNACKBAR_AUTO_HIDE_MS,
    ) => {
      setMessage(msg);
      setSeverity(sev);
      setAutoHideDuration(autoHideDurationMs);
      setOpen(true);
    },
    [],
  );

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <SnackbarContext.Provider value={{ showMessage }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={autoHideDuration}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          zIndex: (theme) =>
            theme.zIndex.modal +
            ELEVATED_DIALOG_Z_INDEX_OFFSET +
            GLOBAL_SNACKBAR_ABOVE_ELEVATED_Z_INDEX_OFFSET,
        }}
      >
        <Alert
          severity={severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};
